/* prettier-ignore-file */
import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Switch, ActivityIndicator } from 'react-native';

const WaiverTemplateEditor = ({ initialTemplate, onSave, storeName = "Our Store" }) => {
  const [sections, setSections] = useState(initialTemplate?.sections || []);
  const [finalSection, setFinalSection] = useState(initialTemplate?.finalSignatureSection || {
    title: "FULL SIGNATURE",
    content: "By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions contained in this Liability Waiver and Release.",
    requiresTypedName: true,
    requiresHomeAddress: true,
    requiresSignature: true
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);

  // Add new section
  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: "NEW SECTION",
      content: "Enter section content here...",
      requiresInitial: false,
      order: sections.length + 1,
      visible: true
    };
    setSections([...sections, newSection]);
  };

  // Remove section
  const removeSection = (id) => {
    if (window.confirm("Remove this section?")) {
      setSections(sections.filter(s => s.id !== id));
    }
  };

  // Update section
  const updateSection = (id, field, value) => {
    setSections(sections.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  // Move section up/down
  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    // Update order numbers
    newSections.forEach((section, idx) => {
      section.order = idx + 1;
    });
    
    setSections(newSections);
  };

  // Save template
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        sections,
        finalSignatureSection: finalSection,
        lastUpdated: new Date().toISOString()
      });
      alert('✅ Waiver template saved successfully!');
    } catch (error) {
      console.error('Error saving waiver template:', error);
      alert('❌ Failed to save waiver template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Preview renderer
  const renderPreview = () => {
    return (
      <ScrollView style={styles.preview}>
        <View style={styles.previewContainer}>
          <Text style={styles.previewTitle}>LIABILITY WAIVER AND RELEASE</Text>
          <Text style={styles.previewSubtitle}>{storeName} - Equipment Rental Agreement</Text>
          
          <View style={styles.previewSection}>
            <Text style={styles.previewSectionTitle}>PARTICIPANT INFORMATION</Text>
            <Text style={styles.previewText}>Participant Name: [Customer Name]</Text>
            <Text style={styles.previewText}>Date: {new Date().toLocaleDateString()}</Text>
          </View>

          {sections.filter(s => s.visible).map((section, index) => (
            <View key={section.id} style={styles.previewSection}>
              <Text style={styles.previewSectionTitle}>
                {section.order}. {section.title}
              </Text>
              <Text style={styles.previewText}>{section.content}</Text>
                          </View>
          ))}

          <View style={styles.previewSection}>
            <Text style={styles.previewSectionTitle}>{finalSection.title}</Text>
            <Text style={styles.previewText}>{finalSection.content}</Text>
            {finalSection.requiresTypedName && (
              <Text style={styles.previewFieldLabel}>Type Your Full Name: _______</Text>
            )}
            {finalSection.requiresHomeAddress && (
              <Text style={styles.previewFieldLabel}>Home Address: _______</Text>
            )}
            {finalSection.requiresSignature && (
              <Text style={styles.previewFieldLabel}>Signature: _______</Text>
            )}
          </View>

          <View style={styles.previewSection}>
            <Text style={styles.previewText}>
              ☑️ I have read and understand this waiver. I voluntarily agree to be bound by all terms and conditions.
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Waiver Template Editor</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.modeButton, !previewMode && styles.modeButtonActive]}
            onPress={() => setPreviewMode(false)}
          >
            <Text style={[styles.modeButtonText, !previewMode && styles.modeButtonTextActive]}>
              ✏️ Edit
            </Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.modeButton, previewMode && styles.modeButtonActive]}
            onPress={() => setPreviewMode(true)}
          >
            <Text style={[styles.modeButtonText, previewMode && styles.modeButtonTextActive]}>
              👁️ Preview
            </Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>💾 Save Template</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Content */}
      {previewMode ? (
        renderPreview()
      ) : (
        <ScrollView style={styles.editor}>
          <View style={styles.editorContainer}>
            
            {/* Info Banner */}
            <View style={styles.infoBanner}>
              <Text style={styles.infoBannerText}>
                ℹ️ Create customizable sections with optional initial requirements. 
                Sections will display in order on the waiver page.
              </Text>
            </View>

            {/* Available Variables Banner */}
            <View style={[styles.infoBanner, { backgroundColor: '#FDF3E3', borderColor: '#B4761B' }]}>
              <Text style={[styles.infoBannerText, { color: '#7A4E12' }]}>
                📝 Available Variables: {'{storeName}'}, {'{storeAddress}'}, {'{customerName}'}, {'{date}'}
              </Text>
              <Text style={[styles.infoBannerText, { color: '#7A4E12', fontSize: 12, marginTop: 4 }]}>
                Use these in your waiver text and they will be automatically replaced with actual values.
              </Text>
            </View>

            {/* Sections */}
            <Text style={styles.editorLabel}>Waiver Sections</Text>
            {sections.map((section, index) => (
              <View key={section.id} style={styles.sectionCard}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionNumber}>Section {section.order}</Text>
                  <View style={styles.sectionActions}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.actionButton}
                      onPress={() => moveSection(index, 'up')}
                      disabled={index === 0}
                    >
                      <Text style={styles.actionButtonText}>↑</Text>
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.actionButton}
                      onPress={() => moveSection(index, 'down')}
                      disabled={index === sections.length - 1}
                    >
                      <Text style={styles.actionButtonText}>↓</Text>
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.actionButton, styles.deleteButton]}
                      onPress={() => removeSection(section.id)}
                    >
                      <Text style={styles.deleteButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                <Text style={styles.fieldLabel}>Section Title</Text>
                <TextInput
                  style={styles.input}
                  value={section.title}
                  onChangeText={(text) => updateSection(section.id, 'title', text)}
                  placeholder="Section title..."
                  placeholderTextColor="#8A9794"
                />

                <Text style={styles.fieldLabel}>Section Content</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={section.content}
                  onChangeText={(text) => updateSection(section.id, 'content', text)}
                  placeholder="Enter the legal text for this section..."
                  placeholderTextColor="#8A9794"
                  multiline
                  numberOfLines={6}
                />


                <View style={styles.switchRow}>
                  <Text style={styles.switchLabel}>Visible</Text>
                  <Switch
                    value={section.visible}
                    onValueChange={(value) => updateSection(section.id, 'visible', value)}
                  />
                </View>
              </View>
            ))}

            <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.addButton} onPress={addSection}>
              <Text style={styles.addButtonText}>➕ Add New Section</Text>
            </TouchableOpacity>

            {/* Final Signature Section */}
            <View style={styles.finalSectionCard}>
              <Text style={styles.finalSectionTitle}>Final Signature Section</Text>
              
              <Text style={styles.fieldLabel}>Section Title</Text>
              <TextInput
                style={styles.input}
                value={finalSection.title}
                onChangeText={(text) => setFinalSection({...finalSection, title: text})}
                placeholder="Final section title..."
                placeholderTextColor="#8A9794"
              />

              <Text style={styles.fieldLabel}>Section Content</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={finalSection.content}
                onChangeText={(text) => setFinalSection({...finalSection, content: text})}
                placeholder="Enter final agreement text..."
                placeholderTextColor="#8A9794"
                multiline
                numberOfLines={4}
              />

              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>Requires Typed Name</Text>
                <Switch
                  value={finalSection.requiresTypedName}
                  onValueChange={(value) => setFinalSection({...finalSection, requiresTypedName: value})}
                />
              </View>

              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>Requires Home Address</Text>
                <Switch
                  value={finalSection.requiresHomeAddress}
                  onValueChange={(value) => setFinalSection({...finalSection, requiresHomeAddress: value})}
                />
              </View>

              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>Requires Signature</Text>
                <Switch
                  value={finalSection.requiresSignature}
                  onValueChange={(value) => setFinalSection({...finalSection, requiresSignature: value})}
                />
              </View>
            </View>

            {/* Variables Help */}
            <View style={styles.helpCard}>
              <Text style={styles.helpTitle}>📝 Available Variables</Text>
              <Text style={styles.helpText}>
                Use these variables in your text (they'll be replaced automatically):
              </Text>
              <Text style={styles.helpVariable}>{'<storeName>'} - Your legal business name</Text>
              <Text style={styles.helpVariable}>{'<storeAddress>'} - Your legal business address</Text>
              <Text style={styles.helpVariable}>{'<customerName>'} - Customer's full name</Text>
              <Text style={styles.helpVariable}>{'<date>'} - Current date</Text>
            </View>

          </View>
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F8F7',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E3A38',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 10,
  },
  modeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#0E7C6B',
    backgroundColor: '#FFFFFF',
  },
  modeButtonActive: {
    backgroundColor: '#0E7C6B',
  },
  modeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0E7C6B',
  },
  modeButtonTextActive: {
    color: '#FFFFFF',
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#1F8A5B',
  },
  saveButtonDisabled: {
    backgroundColor: '#DDE3E2',
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  editor: {
    flex: 1,
  },
  editorContainer: {
    padding: 20,
  },
  infoBanner: {
    backgroundColor: '#E3F2EF',
    padding: 15,
    borderRadius: 6,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#0E7C6B',
  },
  infoBannerText: {
    fontSize: 14,
    color: '#2E3A38',
    lineHeight: 20,
  },
  editorLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 15,
  },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#DDE3E2',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0E7C6B',
  },
  sectionActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 5,
    backgroundColor: '#EDF1F0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#DDE3E2',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0E7C6B',
  },
  deleteButton: {
    backgroundColor: '#FBEAE9',
    borderColor: '#C4453D',
  },
  deleteButtonText: {
    fontSize: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5C6B68',
    marginBottom: 6,
    marginTop: 8,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 5,
    padding: 10,
    fontSize: 14,
    color: '#2E3A38',
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#EDF1F0',
  },
  switchLabel: {
    fontSize: 14,
    color: '#2E3A38',
    fontWeight: '500',
  },
  addButton: {
    backgroundColor: '#0E7C6B',
    padding: 16,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 20,
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  finalSectionCard: {
    backgroundColor: '#FDF3E3',
    padding: 16,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#B4761B',
  },
  finalSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#7A4E12',
    marginBottom: 12,
  },
  helpCard: {
    backgroundColor: '#F4F6F6',
    padding: 16,
    borderRadius: 6,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#DDE3E2',
  },
  helpTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 10,
  },
  helpText: {
    fontSize: 14,
    color: '#5C6B68',
    marginBottom: 10,
  },
  helpVariable: {
    fontSize: 13,
    color: '#0E7C6B',
    fontFamily: 'monospace',
    marginLeft: 10,
    marginBottom: 4,
  },
  // Preview styles
  preview: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  previewContainer: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  previewTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
    color: '#0F1A19',
  },
  previewSubtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: '#5C6B68',
  },
  previewSection: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EDF1F0',
  },
  previewSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#0F1A19',
  },
  previewText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#2E3A38',
    marginBottom: 12,
  },
  previewInitialBox: {
    marginTop: 16,
    marginBottom: 8,
  },
  previewInitialText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#5C6B68',
  },
  previewFieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5C6B68',
    marginTop: 12,
  },
});

export default WaiverTemplateEditor;




